"use client"

import { useState } from 'react'
import { User, Building2, Mail, Phone, Cake, Home, Users, School, Edit2 } from "lucide-react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { InfoItem } from "@/components/ui/info-item"
import { useUserProfile } from "@/hooks/stores/userStore"
import { EditPersonalInfoModal } from "./edit-personal-info-modal"

interface PersonalInfoSectionProps {
  userData?: {
    name?: string;
    grade?: string;
    email?: string;
    phoneNumber?: string;
    dateOfBirth?: Date | string;
    address?: string;
    parentName?: string;
    parentEmail?: string;
    parentPhone?: string;
    schoolName?: string;
  };
}

export function PersonalInfoSection({ userData }: PersonalInfoSectionProps) {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  
  // Get user data from Zustand store
  const storeUser = useUserProfile();
  
  // Use prop data if provided, otherwise use store data
  const user = userData || storeUser || {};
  
  // Format date of birth for display
  const formatDateOfBirth = (date: Date | string | undefined) => {
    if (!date) return "Not provided";
    
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date;
      return dateObj.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  // Format phone number for display
  const formatPhoneNumber = (phone: string | undefined) => {
    if (!phone) return "Not provided";
    
    // Clean and format phone number
    const cleaned = phone.replace(/\D/g, '');
    
    if (cleaned.length === 11 && cleaned.startsWith('0')) {
      // Nigerian format: 08123456789 -> +234 812 345 6789
      return `+234 ${cleaned.slice(1, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
    } else if (cleaned.length === 13 && cleaned.startsWith('234')) {
      // +234 format
      return `+${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 9)} ${cleaned.slice(9)}`;
    }
    
    // Return as is if not standard format
    return phone;
  };

  // Extract user data with fallbacks
  const userInfo = {
    name: user?.name || "Not provided",
    grade: user?.grade || "Not provided",
    email: user?.email || "Not provided",
    phoneNumber: user?.phoneNumber,
    dateOfBirth: user?.dateOfBirth,
    address: user?.address,
    parentName: user?.parentName,
    parentEmail: user?.parentEmail,
    parentPhone: user?.parentPhone,
    schoolName: user?.schoolName,
  };

  const handleEditClick = () => {
    setIsEditModalOpen(true)
  }

  return (
    <>
      <Card className="border-slate-100 shadow-lg">
        <CardHeader className="bg-slate-100 text-slate-900">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle className="text-2xl font-bold mb-2">Personal Information</CardTitle>
            </div>
            <Button
              onClick={handleEditClick}
              className="bg-teal-600 hover:bg-teal-700 text-white flex items-center gap-2 shadow-sm hover:shadow-md transition-all"
              size="sm"
            >
              <Edit2 className="w-4 h-4" />
              Edit Information
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-5 p-6">
          {/* Student Information Section */}
          <div className="space-y-4">
            <InfoItem 
              icon={<User className="w-5 h-5 text-blue-600" />} 
              label="Full Name" 
              value={userInfo.name}
              valueClassName="text-slate-900 font-medium"
            />
            
            <InfoItem 
              icon={<Building2 className="w-5 h-5 text-blue-600" />} 
              label="Grade / Class" 
              value={userInfo.grade}
              valueClassName="text-slate-900 font-medium"
            />
            
            <InfoItem 
              icon={<Mail className="w-5 h-5 text-blue-600" />} 
              label="Email" 
              value={userInfo.email}
              valueClassName="text-blue-600 hover:text-blue-700 cursor-pointer transition-colors font-medium"
            />
            
            <InfoItem 
              icon={<Phone className="w-5 h-5 text-blue-600" />} 
              label="Phone Number" 
              value={formatPhoneNumber(userInfo.phoneNumber)}
              valueClassName="text-slate-900 font-medium"
            />
            
            <InfoItem 
              icon={<Cake className="w-5 h-5 text-blue-600" />} 
              label="Date of Birth" 
              value={formatDateOfBirth(userInfo.dateOfBirth)}
              valueClassName="text-slate-900 font-medium"
            />
            
            <InfoItem 
              icon={<Home className="w-5 h-5 text-blue-600" />} 
              label="Address" 
              value={userInfo.address || "Not provided"}
              valueClassName="text-slate-900 font-medium"
            />
            
            <InfoItem 
              icon={<School className="w-5 h-5 text-blue-600" />} 
              label="School" 
              value={userInfo.schoolName || "Not provided"}
              valueClassName="text-slate-900 font-medium"
            />
          </div>
          
          {/* Parent/Guardian Section */}
          <div className="pt-4 border-t border-slate-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-teal-100">
                  <Users className="w-5 h-5 text-teal-700" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-800 text-lg">Parent/Guardian Information</h4>
                  <p className="text-slate-600 text-sm">Primary contact details</p>
                </div>
              </div>
            </div>
            
            <div className="space-y-4 pl-2">
              <InfoItem 
                icon={<User className="w-4 h-4 text-teal-600" />} 
                label="Parent Name" 
                value={userInfo.parentName || "Not provided"}
                compact
                valueClassName="text-slate-900 font-medium"
              />
              
              <InfoItem 
                icon={<Mail className="w-4 h-4 text-teal-600" />} 
                label="Parent Email" 
                value={userInfo.parentEmail || "Not provided"}
                compact
                valueClassName="text-blue-600 hover:text-blue-700 cursor-pointer transition-colors font-medium"
              />
              
              <InfoItem 
                icon={<Phone className="w-4 h-4 text-teal-600" />} 
                label="Parent Phone" 
                value={formatPhoneNumber(userInfo.parentPhone)}
                compact
                valueClassName="text-slate-900 font-medium"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Edit Modal */}
      <EditPersonalInfoModal 
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
      />
    </>
  )
}